import React, { useState } from "react";
import { Card, Flex, Text, Button, Badge } from "@radix-ui/themes";
import { useNavigate } from "react-router-dom";
import { PermissionAlertDialog } from "components";
import { AuthDotdigitalDialog } from "./AuthDotdigitalDialog";
import { useValidateDotdigital, useGetDotdigitalStatus, useSaveDotdigitalConfigs } from "hooks";

const DOTDIGITAL_REGION_OPTIONS = [
  "https://r1-app.dotdigital.com/",
  "https://r2-app.dotdigital.com/",
  "https://r3-app.dotdigital.com/",
];

const DOTDIGITAL_EMAIL_OPT_IN_OPTIONS = [
  { value: "single", label: "Single opt-in" },
  { value: "verifiedDouble", label: "Verified double opt-in" },
];

const DOTDIGITAL_CONTACT_SYNC_AUDIENCE_OPTIONS = [
  { value: "subscribed", label: "Only opted-in guests" },
  { value: "all", label: "All guests whether opted-in or not" },
];

function toStringValue(value) {
  if (value === null || typeof value === "undefined") {
    return "";
  }

  return String(value).trim();
}

function normalizeRegion(region) {
  return toStringValue(region) || DOTDIGITAL_REGION_OPTIONS[0];
}

function normalizeEmailOptInType(value) {
  return DOTDIGITAL_EMAIL_OPT_IN_OPTIONS.some((option) => option.value === value) ? value : "single";
}

function normalizeSyncOnlySubscribedContacts(value) {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    return value !== "all" && !["false", "0", "no", "off"].includes(value.trim().toLowerCase());
  }

  return true;
}

function normalizeDotdigitalLists(lists) {
  return (Array.isArray(lists) ? lists : [])
    .map((list) => ({
      id: toStringValue(list?.id ?? list?.list_id),
      name: toStringValue(list?.name ?? list?.list_name),
    }))
    .filter((list) => list.id || list.name);
}

function getRegionCode(region) {
  const regionValue = toStringValue(region);
  const match = /(?:https?:\/\/)?(r\d+)-app\.dotdigital\.com\/?/i.exec(regionValue);

  if (match?.[1]) {
    return match[1].toUpperCase();
  }

  return regionValue.replace(/^https?:\/\//, "").replace(/\/$/, "") || "Region";
}

function buildDefaultFormData() {
  return {
    apiUser: "",
    apiPassword: "",
    accountRegion: DOTDIGITAL_REGION_OPTIONS[0],
    emailOptInType: "single",
    syncOnlySubscribedContacts: true,
  };
}

function buildLocalConfigKey(config) {
  return `${toStringValue(config?.apiUser).toLowerCase()}::${normalizeRegion(config?.accountRegion)}`;
}

function buildValidatedConfig(config, configId, lists) {
  const persistedId = toStringValue(configId);
  const apiUser = toStringValue(config?.apiUser);
  const accountRegion = normalizeRegion(config?.accountRegion);

  return {
    id: persistedId || buildLocalConfigKey({ apiUser, accountRegion }),
    persistedId: persistedId || null,
    localId: buildLocalConfigKey({ apiUser, accountRegion }),
    apiUser,
    apiPassword: config?.apiPassword || "",
    accountRegion,
    emailOptInType: normalizeEmailOptInType(config?.emailOptInType ?? config?.email_opt_in_type),
    syncOnlySubscribedContacts: normalizeSyncOnlySubscribedContacts(config?.syncOnlySubscribedContacts ?? config?.sync_only_subscribed_contacts),
    lists: normalizeDotdigitalLists(lists),
    label: [apiUser || "Account", getRegionCode(accountRegion)].filter(Boolean).join(" • "),
  };
}

function buildConfigSavePayload(config) {
  return {
    local_id: config?.localId,
    api_user: toStringValue(config?.apiUser),
    api_user_password: config?.apiPassword || "",
    account_region: normalizeRegion(config?.accountRegion),
    email_opt_in_type: normalizeEmailOptInType(config?.emailOptInType),
    sync_only_subscribed_contacts: normalizeSyncOnlySubscribedContacts(config?.syncOnlySubscribedContacts),
    config_id: config?.persistedId || undefined,
  };
}

export function BrowseDotdigitalCard({ userProfile }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(buildDefaultFormData);
  const [dotdigitalConfigs, setDotdigitalConfigs] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isValidatingAccount, setIsValidatingAccount] = useState(false);
  const [isSavingConfigs, setIsSavingConfigs] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [showPermissionAlert, setShowPermissionAlert] = useState(false);
  const [apiError, setApiError] = useState("");

  const { data: statusData } = useGetDotdigitalStatus(userProfile?.organisation_id);
  const validateDotdigital = useValidateDotdigital();
  const saveDotdigitalConfigs = useSaveDotdigitalConfigs();

  const isDotdigitalActive = () => statusData?.data?.is_active === true;

  const resetForm = () => {
    setFormData(buildDefaultFormData());
    setDotdigitalConfigs([]);
    setFieldErrors({});
    setApiError("");
  };

  const handleOpenChange = (isOpen) => {
    setOpenDialog(isOpen);
    if (!isOpen) {
      resetForm();
    }
  };

  const handleConnectClick = () => {
    if (userProfile.user_role === "Owner") {
      handleOpenChange(true);
      return;
    }

    setShowPermissionAlert(true);
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData((previousFormData) => ({
      ...previousFormData,
      [fieldName]: value,
    }));
    setFieldErrors((previousErrors) => ({
      ...previousErrors,
      [fieldName]: false,
    }));
    setApiError("");
  };

  const handleAddAccount = async () => {
    const nextErrors = {
      apiUser: !toStringValue(formData.apiUser),
      apiPassword: !toStringValue(formData.apiPassword),
      accountRegion: !toStringValue(formData.accountRegion),
    };

    if (Object.values(nextErrors).some(Boolean)) {
      setFieldErrors(nextErrors);
      return null;
    }

    const nextConfig = {
      apiUser: toStringValue(formData.apiUser),
      apiPassword: formData.apiPassword || "",
      accountRegion: normalizeRegion(formData.accountRegion),
      emailOptInType: normalizeEmailOptInType(formData.emailOptInType),
      syncOnlySubscribedContacts: normalizeSyncOnlySubscribedContacts(formData.syncOnlySubscribedContacts),
    };

    setIsValidatingAccount(true);
    setApiError("");

    try {
      const result = await validateDotdigital.mutateAsync({
        api_user: nextConfig.apiUser,
        api_user_password: nextConfig.apiPassword,
        account_region: nextConfig.accountRegion,
        email_opt_in_type: nextConfig.emailOptInType,
        sync_only_subscribed_contacts: nextConfig.syncOnlySubscribedContacts,
        organisation_id: userProfile.organisation_id,
        persist: false,
      });

      const validatedConfig = buildValidatedConfig(
        nextConfig,
        result?.data?.config_id || result?.data?.config?.id,
        result?.data?.lists || result?.data?.config?.lists || []
      );

      setDotdigitalConfigs((previousConfigs) => {
        const remainingConfigs = (previousConfigs || []).filter((config) => config.localId !== validatedConfig.localId);
        return [...remainingConfigs, validatedConfig];
      });
      setFormData(buildDefaultFormData());
      setFieldErrors({});
      return validatedConfig;
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to validate Dotdigital credentials. Please check and try again.";
      setApiError(message);
      return null;
    } finally {
      setIsValidatingAccount(false);
    }
  };

  const handleRemoveAccount = (localId) => {
    setDotdigitalConfigs((previousConfigs) => (previousConfigs || []).filter((config) => config.localId !== localId));
    setApiError("");
  };

  const handleContinue = async () => {
    if (dotdigitalConfigs.length === 0) {
      return;
    }

    setIsSavingConfigs(true);
    setApiError("");

    try {
      const result = await saveDotdigitalConfigs.mutateAsync({
        organisation_id: userProfile.organisation_id,
        configs: dotdigitalConfigs.map(buildConfigSavePayload),
      });

      const savedConfigsByLocalId = new Map(
        (result?.data?.configs || []).map((config, index) => [config.local_id || dotdigitalConfigs[index]?.localId, config])
      );
      const persistedConfigs = dotdigitalConfigs.map((config) => {
        const savedConfig = savedConfigsByLocalId.get(config.localId);
        const persistedId = toStringValue(savedConfig?.id ?? savedConfig?.config_id ?? config.persistedId);

        if (!persistedId) {
          throw new Error("Failed to save one or more Dotdigital accounts.");
        }

        const validatedConfig = buildValidatedConfig(config, persistedId, config.lists);
        const { apiPassword, ...safeConfig } = validatedConfig;
        return safeConfig;
      });

      handleOpenChange(false);
      navigate("/dotdigital-setup", {
        state: {
          configs: persistedConfigs,
          orgId: userProfile.organisation_id,
        },
      });
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Failed to save Dotdigital accounts. Please try again.";
      setApiError(message);
    } finally {
      setIsSavingConfigs(false);
    }
  };

  return (
    <>
      <Card
        style={{
          display: "flex",
          width: "100%",
          padding: "var(--space-4)",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Flex align="center" gap="4">
          <Flex
            height="40px"
            width="40px"
            p="2"
            justify="center"
            align="center"
            style={{
              borderRadius: "var(--space-1)",
              background: "#FFFFFF",
              border: "1px solid var(--gray-a6)",
            }}
          >
            <img src={require("../../../assets/images/dotdigtal.png")} alt="dotdigital" style={{ width: "24px" }} />
          </Flex>
          <Flex direction="column" justify="center" align="start">
            <Text size="4" weight="medium">
              Dotdigital
            </Text>
            <Text size="1" color="gray">
              CRM & Email marketing
            </Text>
          </Flex>
        </Flex>

        <Flex align="center">
          {isDotdigitalActive() ? (
            <Badge size="1" variant="soft" color="green">
              Connected
            </Badge>
          ) : (
            <Button onClick={handleConnectClick} size="2" variant="soft" color="gray">
              Connect
            </Button>
          )}
        </Flex>
      </Card>

      <AuthDotdigitalDialog
        isOpen={openDialog}
        onOpenChange={handleOpenChange}
        onCancel={resetForm}
        onContinue={handleContinue}
        onAddAccount={handleAddAccount}
        onRemoveAccount={handleRemoveAccount}
        dotdigitalConfigs={dotdigitalConfigs}
        formData={formData}
        onFieldChange={handleFieldChange}
        fieldErrors={fieldErrors}
        emailOptInOptions={DOTDIGITAL_EMAIL_OPT_IN_OPTIONS}
        contactSyncAudienceOptions={DOTDIGITAL_CONTACT_SYNC_AUDIENCE_OPTIONS}
        isAddAccountLoading={isValidatingAccount}
        isContinueLoading={isSavingConfigs}
        isContinueDisabled={dotdigitalConfigs.length === 0}
        apiError={apiError}
      />

      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />
    </>
  );
}
